
import { TablePage, Dialog, GroupList } from 'web-mojo';
import { DeviceList } from '@portal/models/Device.js';
import '@portal/devices/DeviceView.js';

export default class DevicePage extends TablePage {
    constructor(options = {}) {
        super({
            title: 'Devices',
            icon: "bi-hdd-network",
            pageName: 'device_list',
            route: "group/devices",
            Collection: DeviceList,
            permissions: ["view_cam_devices", "manage_cam_devices"],
            requiresGroup: true,
            viewDialogOptions: {
                header: false,
                size: "md"
            },

            defaultQuery: {
                group: () => app.activeGroup?.id,
                sort: '-id',
                // Hide deactivated (disabled) devices by default; the Enabled filter reveals them.
                is_enabled: true,
            },

            columns: [
                {
                    label: "Online",
                    key: "is_online",
                    // filled = online, hollow = offline — distinct without relying on color alone
                    formatter: "yesnoicon('bi bi-circle-fill text-success', 'bi bi-circle text-secondary')",
                },
                {
                    label: "Enabled",
                    key: "is_enabled",
                    // check = enabled, x = deactivated/disabled
                    formatter: "yesnoicon('bi bi-check-circle-fill text-success', 'bi bi-x-circle text-secondary')",
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Enabled', value: true },
                            { label: 'Disabled', value: false }
                        ]
                    }
                },
                {
                    label: "Lock",
                    key: "lock_engaged",
                    formatter: "yesnoicon('bi bi-lock-fill text-danger', 'bi bi-unlock-fill text-success')",
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Locked', value: 'true' },
                            { label: 'Unlocked', value: 'false' }
                        ]
                    }
                },
                {
                    label: "Name",
                    key: "name",
                    formatter: "default('not set')"
                },
                {
                    label: "Serial",
                    key: "uuid",
                    visibility: 'lg'
                },
                {
                    label: "Last Seen",
                    key: "last_seen",
                    formatter: "relative",
                },
            ],

            selectable: true,
            batchActions: [
                { label: "Export", icon: "bi bi-download", action: "export" },
                { label: "Move", icon: "bi bi-arrows-move", action: "move" },
                { label: "Enable", icon: "bi bi-check-circle", action: "enable" },
                { label: "Disable", icon: "bi bi-x-circle", action: "disable" },
            ],
            searchable: true,
            sortable: true,
            filterable: true,
            paginated: true,

            showRefresh: true,
            showAdd: false,
            showExport: true,

            tableOptions: {
                striped: true,
                bordered: false,
                hover: true,
                responsive: false
            },

            tableViewOptions: {
                hideActivePillNames: ["group"]
            },

            ...options,
        });
    }

    async onActionBatchExport() {
        this.tableView.collection.export();
    }

    async onActionBatchMove() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        const data = await Dialog.showForm({
            title: `Move ${selected.length} device${selected.length > 1 ? 's' : ''}`,
            fields: [{
                type: 'collection',
                name: 'group',
                label: 'Target Group',
                Collection: GroupList,
                labelField: 'name',
                valueField: 'id',
                maxItems: 10,
                placeholder: 'Search groups...',
                emptyFetch: false,
                debounceMs: 300,
            }]
        });
        if (!data?.group) return;
        await Promise.all(selected.map(item => item.model.save({ group: data.group })));
        app.toast.success(`Moved ${selected.length} device${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchEnable() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        await Promise.all(selected.map(item => item.model.save({ is_enabled: true })));
        app.toast.success(`Enabled ${selected.length} device${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchDisable() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        const confirmed = await Dialog.confirm({
            title: `Disable ${selected.length} device${selected.length > 1 ? 's' : ''}`,
            message: 'Disable selected devices?',
            confirmText: 'Disable',
            confirmClass: 'btn-danger',
        });
        if (!confirmed) return;
        await Promise.all(selected.map(item => item.model.save({ is_enabled: false })));
        app.toast.success(`Disabled ${selected.length} device${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }
}
